import { Colors } from "@/enums/Colors";
import {
  CircularProgress,
  FilledInputProps,
  InputAdornment,
  InputProps,
  OutlinedInputProps,
  SxProps,
  TextField,
  TextFieldProps,
  styled,
} from "@mui/material";
import { ChangeEvent, FC, MutableRefObject } from "react";
import { useTranslation } from "react-i18next";
import { SearchIcon } from "./icons/SearchIcon";

interface Props extends Omit<TextFieldProps, "onChange" | "value" | "color"> {
  value?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  placeHolder?: string;
  hideIcon?: boolean;
  icon?: JSX.Element;
  sx?: SxProps;
  customRef?: MutableRefObject<HTMLInputElement | null>;
  color?: string;
  loading?: boolean;
  defaultValue?: string;
  InputProps?: Partial<FilledInputProps> | Partial<OutlinedInputProps> | Partial<InputProps>;
}

export const InputField: FC<Props> = ({
  onChange,
  value,
  placeHolder,
  hideIcon,
  icon,
  sx,
  customRef,
  color,
  loading,
  defaultValue,
  InputProps,
  ...rest
}) => {
  const { t } = useTranslation("common");
  return (
    <StyledInput
      sx={sx}
      onChange={(e: ChangeEvent<HTMLInputElement>) => {
        onChange && onChange(e);
      }}
      value={value}
      inputRef={customRef}
      type="text"
      defaultValue={defaultValue}
      placeholder={placeHolder || t("placeholders.typeHere")}
      {...rest}
      InputProps={{
        inputProps: {
          "::placeholder": {
            color: color ? color : Colors.LIGHT_GRAY_BFC2C6,
          },
        },
        startAdornment: !hideIcon && (
          <InputAdornment position="start">{icon != undefined ? icon : <SearchIcon sxClass={{ fontSize: "3rem" }} />}</InputAdornment>
        ),
        endAdornment: loading && (
          <InputAdornment position="end">
            <CircularProgress sx={{ color: Colors.LIGHT_GRAY_8E8EA0 }} size={18} />
          </InputAdornment>
        ),
        ...InputProps,
      }}
    />
  );
};

const StyledInput = styled(TextField)<{ color?: string }>(({ color }) => ({
  "& .MuiInputBase-root": {
    width: "100%",
    display: "flex",
    height: "2.75rem",
    borderRadius: "0.5rem",
    backgroundColor: "white",
    padding: "0.75rem",
    alignItems: "center",
    justifyContent: "space-between",
    fontSize: "0.875rem",
    border: "none !important",
    "& .MuiOutlinedInput-notchedOutline": {
      border: `1px solid ${color ? color : Colors.LIGHT_GRAY_BFC2C6}`,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: `1px solid ${color ? color : Colors.LIGHT_GRAY_BFC2C6}`,
    },
    "&:hover .MuiOutlinedInput-notchedOutline ": {
      border: `1px solid ${color ? color : Colors.LIGHT_GRAY_BFC2C6}`,
    },
  },
}));
